import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { map, timeout } from 'rxjs/operators';
import { Observable, lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ClientDetailsService {
  private readonly ipProviderUrl = 'https://api64.ipify.org/?format=json';

  private readonly geoLocationUrl = 'https://nominatim.openstreetmap.org/reverse';

  private readonly nominatimHeaders = new HttpHeaders({
    'User-Agent': 'YourAppName/1.0 (contact@yourdomain.com)'
  });

  ipAddress: string = '';
  latitude: number = 0;
  longitude: number = 0;
  locationError: string = '';
  formattedTime: string = '';
  deviceInfo: DeviceInfo | null = null;

  baseEndPoint = environment.apiURL;

  constructor(
    private http: HttpClient,
    private deviceService: DeviceDetectorService
  ) {}

  async getIPAddress(): Promise<void> {
    const response: any = await lastValueFrom(
      this.http.get(this.ipProviderUrl).pipe(timeout(5_000))
    );
    const ip = response?.ip ?? '';
    if (!ip) {
      throw new Error('IP address provider is unreachable. Check network connectivity.');
    }
    this.ipAddress = ip;
  }

  getGeoLocation(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('Geolocation API is not supported by this browser.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude;
          this.longitude = position.coords.longitude;
          resolve();
        },
        (error) => {
          const messages: Record<number, string> = {
            1: 'Location permission denied by the user. While clicking on OK you will be proceeding with signing without location details captured in audit trail. We recommend you allow location access for better audit trail and security.',
            2: 'Location information is unavailable. While clicking on OK you will be proceeding with signing without location details captured in audit trail. We recommend you kindly contact doqfy support for further assistance.',
            3: 'Location request timed out. While clicking on OK you will be proceeding with signing without location details captured in audit trail. We recommend you kindly contact doqfy support for further assistance.',
          };
          reject(new Error(messages[error.code] ?? 'Unknown geolocation error.'));
        },
        {
          enableHighAccuracy: true,
          timeout: 10_000,
          maximumAge: 0,
        }
      );
    });
  }

  getLocationDetails(latitude: number, longitude: number): Observable<any> {
    const params = new HttpParams()
      .set('format', 'json')
      .set('lat', latitude.toString())
      .set('lon', longitude.toString())
      .set('zoom', '18')
      .set('addressdetails', '1');

    return this.http
      .get<any>(this.geoLocationUrl, { params, headers: this.nominatimHeaders })
      .pipe(map((response) => response ?? null));
  }

  getDeviceData(): void {
    this.deviceInfo = this.deviceService.getDeviceInfo();
  }

  getTimeStamp(): void {
    this.formattedTime = new Date().toLocaleString();
  }

  async getClientDetails(): Promise<{
    ip_address: string;
    latitude: number;
    longitude: number;
    device_info: DeviceInfo | null;
    timeStamp: string;
  }> {
    const failed: string[] = [];

    try {
      await this.getGeoLocation();
    } catch (error: any) {
      console.error('[ClientDetails] Geolocation failed:', error.message);
      failed.push(`Location/GeoFencing — ${error.message}`);
      this.latitude = 0;
      this.longitude = 0;
    }

    try {
      await this.getIPAddress();
    } catch (error: any) {
      console.error('[ClientDetails] IP fetch failed (all providers):', error.message);
      failed.push(`IP Address — ${error.message}`);
      this.ipAddress = 'Unknown';
    }

    try {
      this.getDeviceData();
      if (!this.deviceInfo) {
        throw new Error('DeviceDetectorService returned null.');
      }
    } catch (error: any) {
      console.error('[ClientDetails] Device info failed:', error.message);
      failed.push(`Device Info — ${error.message}`);
    }

    try {
      this.getTimeStamp();
    } catch (error: any) {
      console.error('[ClientDetails] Timestamp failed:', error.message);
      failed.push(`Timestamp — ${error.message}`);
      this.formattedTime = new Date().toISOString();
    }

    if (failed.length > 0) {
      const list = failed.map((f) => `  • ${f}`).join('\n');
      alert(
        `⚠️ Could not retrieve the following client details:\n\n${list}\n\n` +
        `Please check location permissions and network connectivity, then try again.`
      );
    }

    return {
      ip_address: this.ipAddress,
      latitude: this.latitude,
      longitude: this.longitude,
      device_info: this.deviceInfo,
      timeStamp: this.formattedTime,
    };
  }
}
